import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  alpha,
  useTheme,
  Chip,
} from '@mui/material';
import {
  Rocket as RocketIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const CTA: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, 
          ${theme.palette.primary.dark} 0%, 
          ${theme.palette.primary.main} 50%,
          ${theme.palette.secondary.main} 100%)`,
      }}
    >
      {/* Animated Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Floating Circles */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: alpha('#ffffff', 0.05),
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: alpha('#ffffff', 0.05),
          filter: 'blur(80px)',
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha('#ffffff', 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(10px)',
              border: `2px solid ${alpha('#ffffff', 0.3)}`,
            }}
          >
            <RocketIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          {/* Headline */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 900,
              color: 'white',
              lineHeight: 1.2,
            }}
          >
            Ready to Transform Your Project Management?
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              color: alpha('#ffffff', 0.95),
              maxWidth: '600px',
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            Join thousands of teams already using our platform to deliver better software,
            faster. Start your free trial today—no credit card required.
          </Typography>

          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              mt: 2,
              px: 5,
              py: 2,
              fontSize: '1.25rem',
              fontWeight: 700,
              borderRadius: 3,
              bgcolor: 'white',
              color: theme.palette.primary.main,
              boxShadow: `0 8px 32px ${alpha('#000000', 0.2)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'white',
                transform: 'translateY(-4px) scale(1.02)',
                boxShadow: `0 16px 48px ${alpha('#000000', 0.3)}`,
              },
            }}
          >
            Start Free Trial →
          </Button>

          {/* Benefits List */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 4 }}
            sx={{ mt: 4 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: alpha('#ffffff', 0.9), fontSize: '1.25rem' }} />
              <Typography
                variant="body1"
                sx={{ color: alpha('#ffffff', 0.95), fontWeight: 500 }}
              >
                14-day free trial
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: alpha('#ffffff', 0.9), fontSize: '1.25rem' }} />
              <Typography
                variant="body1"
                sx={{ color: alpha('#ffffff', 0.95), fontWeight: 500 }}
              >
                No credit card required
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: alpha('#ffffff', 0.9), fontSize: '1.25rem' }} />
              <Typography
                variant="body1"
                sx={{ color: alpha('#ffffff', 0.95), fontWeight: 500 }}
              >
                Cancel anytime
              </Typography>
            </Stack>
          </Stack>

          {/* Trust Badge */}
          <Chip
            label="🔒 Enterprise-grade security & 99.9% uptime"
            sx={{
              mt: 3,
              bgcolor: alpha('#ffffff', 0.15),
              backdropFilter: 'blur(10px)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.875rem',
              px: 2,
              py: 0.5,
              border: `1px solid ${alpha('#ffffff', 0.2)}`,
            }}
          />
        </Stack>
      </Container>
    </Box>
  );
};

export default CTA;

